import { useState } from "react";
import type { EvidenceSoc2PreflightReportDto } from "@excellent/ui/api/contracts/evidence";
import { ActionButton, ButtonRow, Callout, ToneBadge } from "./ui";

type FrameworkLens = "neutral" | "soc2";
type AuditPeriod = EvidenceSoc2PreflightReportDto["auditPeriod"];

function draftAuditPeriod(start: string, end: string): AuditPeriod | null {
  if (!start || !end || end < start) return null;
  return { id: `${start}:${end}`, start, end };
}

export function FrameworkSettings({
  onNavigate,
}: {
  onNavigate: (to: string) => void;
}) {
  const [framework, setFramework] = useState<FrameworkLens>("neutral");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const soc2Enabled = framework === "soc2";
  const period = draftAuditPeriod(periodStart, periodEnd);
  const invalidPeriod = Boolean(periodStart && periodEnd && !period);

  const chooseFramework = (next: FrameworkLens) => {
    setFramework(next);
    if (next === "neutral") {
      setPeriodStart("");
      setPeriodEnd("");
    }
  };

  return (
    <section
      id="frameworks"
      aria-labelledby="framework-settings-title"
      data-framework-settings=""
      data-read-only-on-open="true"
      data-period-mode="draft"
    >
      <div className="vp-card">
        <div className="vp-card-head">
          <div>
            <h2 className="vp-card-title" id="framework-settings-title">
              Framework and period
            </h2>
            <p className="vp-card-sub">
              Draft a reporting period before opening existing verification tools.
            </p>
          </div>
          <div className="vp-card-head-actions">
            <ToneBadge tone={soc2Enabled ? "accent" : "neutral"}>
              {soc2Enabled ? "SOC 2 draft" : "Framework-neutral"}
            </ToneBadge>
          </div>
        </div>
        <div className="vp-card-body">
          <div>
            <label className="vp-label" htmlFor="framework-lens">Framework lens</label>
            <select
              id="framework-lens"
              className="vp-select"
              value={framework}
              onChange={(event) => chooseFramework(event.target.value as FrameworkLens)}
            >
              <option value="neutral">Framework-neutral</option>
              <option value="soc2">SOC 2</option>
            </select>
            <p className="vp-help">
              {soc2Enabled
                ? "Choose the audit period you want to prepare. Nothing is saved yet."
                : "Keep workflows, checks, evidence, findings, and receipts in their native model."}
            </p>
          </div>

          {!soc2Enabled ? (
            <Callout title="No framework is applied">
              Opening Settings does not enable SOC 2, read run history, run checks, or write configuration.
            </Callout>
          ) : (
            <div className="vp-form" style={{ marginTop: "calc(var(--spacing) * 4)" }}>
              <div>
                <label className="vp-label" htmlFor="soc2-period-start">Period start</label>
                <input
                  id="soc2-period-start"
                  className="vp-input"
                  type="date"
                  value={periodStart}
                  onChange={(event) => setPeriodStart(event.target.value)}
                />
              </div>
              <div>
                <label className="vp-label" htmlFor="soc2-period-end">Period end</label>
                <input
                  id="soc2-period-end"
                  className="vp-input"
                  type="date"
                  value={periodEnd}
                  min={periodStart || undefined}
                  onChange={(event) => setPeriodEnd(event.target.value)}
                  aria-invalid={invalidPeriod || undefined}
                  aria-describedby={invalidPeriod ? "soc2-period-error" : "soc2-period-help"}
                />
                <p className="vp-help" id="soc2-period-help">
                  This draft stays in this view. It is not saved and does not scope evidence yet.
                </p>
              </div>

              {invalidPeriod ? (
                <Callout tone="fail" title="Check the period">
                  <span id="soc2-period-error">The end date must be on or after the start date.</span>
                </Callout>
              ) : null}

              {period ? (
                <div
                  className="vp-fields"
                  data-selected-soc2-period={period.id}
                  aria-live="polite"
                >
                  <h3 className="vp-label">Draft period</h3>
                  <div className="vp-field">
                    <span className="vp-field-label">Start</span>
                    <span className="vp-field-value">{period.start}</span>
                  </div>
                  <div className="vp-field">
                    <span className="vp-field-label">End</span>
                    <span className="vp-field-value">{period.end}</span>
                  </div>
                  <ButtonRow>
                    <ActionButton
                      actionId="settings.framework.open-verification"
                      onClick={() => onNavigate("/agents/verification")}
                    >
                      Open verification
                    </ActionButton>
                    <ActionButton
                      actionId="settings.framework.browse-controls"
                      onClick={() => onNavigate("/controls")}
                    >
                      Browse controls
                    </ActionButton>
                    <ActionButton
                      actionId="settings.framework.browse-evidence"
                      onClick={() => onNavigate("/evidence")}
                    >
                      Browse evidence
                    </ActionButton>
                  </ButtonRow>
                </div>
              ) : null}
            </div>
          )}

          <details style={{ marginTop: "calc(var(--spacing) * 4)" }}>
            <summary>Capability boundary</summary>
            <p className="vp-help">
              This settings view does not persist a framework, create or run a preflight, or create,
              sign, replay, or independently verify receipts. Check those properties on the selected
              receipt or export before relying on them.
            </p>
          </details>
        </div>
      </div>
    </section>
  );
}
